// Global "creator mode" flag.
//
// The creator/edit view of each content type lives at a paired `-creator`
// route (e.g. /documents ↔ /documents-creator). Historically the per-screen
// toggle just navigated between the two. This flag makes the mode global: it
// is synced from the URL whenever a paired list route is visited (see
// routes.js) and drives the main navigation so the chosen mode carries across
// every screen.

import { client } from "../config";

/**
 * Content list routes that have a paired "-creator" variant. Used both to
 * sync the flag from the URL and to build mode-aware navigation links.
 */
export const CREATOR_MODE_ROUTES = new Set([
  "news",
  "events",
  "videos",
  "galleries",
  "podcasts",
  "documents",
  "training",
  "quizzes",
  "links",
  "polls",
  "forms",
  "company_directory",
  "presentations",
]);

// Clients that have creator screens. equans is absent because none of its
// routes are mode-aware, so the toggle would be a dead control there.
const CREATOR_MODE_CLIENTS = ["companyapp", "radisson", "revo-h"];

// Of those, the clients gating creator mode on
// `access_presentation_creator_mode`. Their only list screen is presentations,
// so the permission decides both whether the mode is offered and whether it
// starts on. companyapp is absent from the default-on half on purpose: the
// flag covers every content type there, so defaulting it on would move
// editors into creator mode app-wide.
const PERMISSION_GATED = ["radisson", "revo-h"];

/**
 * Whether creator mode is available to this user at all. Gates the header
 * switch and the mode-aware routes off the same rule so they can't drift.
 */
export const canUseCreatorMode = (permissions) => {
  if (!CREATOR_MODE_CLIENTS.includes(client)) return false;
  if (!PERMISSION_GATED.includes(client)) return true;
  return permissions?.access_presentation_creator_mode === true;
};

const defaultState = {
  creatorMode: false,
  // Set once the user has used a toggle. Until then the flag is seeded from
  // permissions, so an explicit "off" is never overwritten by a later
  // permissions refresh.
  userSet: false,
};

export const creatorModeReducer = (state = defaultState, action) => {
  switch (action.type) {
    case "SET_CREATOR_MODE":
      return { creatorMode: !!action.payload, userSet: true };

    case "SAVE_PERMISSIONS":
      if (state.userSet || !PERMISSION_GATED.includes(client)) {
        return state;
      }

      return {
        ...state,
        creatorMode:
          action.payload?.access_presentation_creator_mode === true,
      };

    case "REMOVE_SESSION":
      return defaultState;

    default:
      return state;
  }
};
